import { localSettingsService } from './localSettingsService';
import { SessionStorageService } from './storage/sessionStorageService';
import { STORAGE_KEYS } from './storage/storageTypes';

export const PERFORMANCE_EVENT = {
    LABEL_DIALOG_LOADING_START: 'LabelDialogLoadingStart',
    LABEL_DIALOG_LOADING_COMPLETE: 'LabelDialogLoadingComplete',
    LABEL_DIALOG_APPLY_METADATA_START: 'LabelDialogApplyMetadataStart',
    LABEL_DIALOG_APPLY_METADATA_COMPLETE: 'LabelDialogApplyMetadataComplete',
    POLICY_DIALOG_LOADED: 'PolicyDialogLoaded',
    POLICY_DIALOG_COMPLETE: 'PolicyDialogComplete',
    SEND_PROCESSING_START: 'SendProcessingStart',
    SEND_PROCESSINGS_COMPLETE: 'SendProcessingComplete',
} as const;

export const TIMING_STORAGE_KEY = STORAGE_KEYS.PERFORMANCE_TIMINGS;
type PerformanceEvent = (typeof PERFORMANCE_EVENT)[keyof typeof PERFORMANCE_EVENT];
type PerformanceEventRecord = Record<PerformanceEvent, number | null>;

export class PerformanceTimingService {
    private eventTimes: PerformanceEventRecord;

    constructor() {
        this.eventTimes = this.loadEventTimes();
    }

    public recordEvent(eventName: PerformanceEvent, eventTimeMs?: number): void {
        if (!this.isEnabled()) {
            return;
        }

        this.saveEventTime(eventName, eventTimeMs ?? Date.now());
        this.logWorkflowTiming(eventName);
    }

    public clearEvent(eventName: PerformanceEvent): void {
        this.saveEventTime(eventName, null);
    }

    private logWorkflowTiming(eventName: PerformanceEvent): void {
        let startEvent: PerformanceEvent | undefined;

        switch (eventName) {
            case PERFORMANCE_EVENT.LABEL_DIALOG_LOADING_COMPLETE:
                // label dialog load start -> label dialog load complete
                startEvent = PERFORMANCE_EVENT.LABEL_DIALOG_LOADING_START;
                break;
            case PERFORMANCE_EVENT.POLICY_DIALOG_LOADED:
                if (this.eventTimes.LabelDialogApplyMetadataStart) {
                    // apply metadata start -> policy check loaded
                    startEvent = PERFORMANCE_EVENT.LABEL_DIALOG_APPLY_METADATA_START;
                } else if (this.eventTimes.SendProcessingStart) {
                    // send start -> policy check loaded
                    startEvent = PERFORMANCE_EVENT.SEND_PROCESSING_START;
                }
                break;
            case PERFORMANCE_EVENT.LABEL_DIALOG_APPLY_METADATA_COMPLETE:
                if (this.eventTimes.PolicyDialogComplete) {
                    // policy dialog complete -> apply metadata complete
                    startEvent = PERFORMANCE_EVENT.POLICY_DIALOG_COMPLETE;
                } else {
                    // apply metadata start -> apply metadata complete
                    startEvent = PERFORMANCE_EVENT.LABEL_DIALOG_APPLY_METADATA_START;
                }
                break;
            case PERFORMANCE_EVENT.SEND_PROCESSINGS_COMPLETE:
                if (this.eventTimes.PolicyDialogComplete) {
                    // policy dialog complete -> send processing complete
                    startEvent = PERFORMANCE_EVENT.POLICY_DIALOG_COMPLETE;
                } else {
                    // send start -> send complete
                    startEvent = PERFORMANCE_EVENT.SEND_PROCESSING_START;
                }
                break;
        }

        if (startEvent) {
            const endTime = this.eventTimes[eventName];
            const startTime = this.eventTimes[startEvent];

            if (startTime && endTime) {
                const elapsedMs = endTime - startTime;

                console.log(`[PerformanceTiming] ${startEvent} -> ${eventName}: elapsedMs=${elapsedMs}`);
            }

            this.clearEvent(eventName);
            this.clearEvent(startEvent);
        }
    }

    private isEnabled(): boolean {
        return localSettingsService.getLocalSettings().logPerformanceTimings === true;
    }

    private loadEventTimes(): PerformanceEventRecord {
        try {
            const storedObj = SessionStorageService.load<Partial<PerformanceEventRecord>>(TIMING_STORAGE_KEY);
            return (this.eventTimes = { ...this.createEmptyRecord(), ...storedObj });
        } catch {}

        return this.createEmptyRecord();
    }

    private saveEventTime(eventName: PerformanceEvent, eventTime: number | null): void {
        this.loadEventTimes();
        this.eventTimes[eventName] = eventTime;
        SessionStorageService.save(this.eventTimes, TIMING_STORAGE_KEY);
    }

    private createEmptyRecord(): PerformanceEventRecord {
        return Object.fromEntries(Object.values(PERFORMANCE_EVENT).map((v) => [v, null])) as PerformanceEventRecord;
    }
}

export const performanceTimingService = new PerformanceTimingService();
